import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Heart } from "lucide-react";

function useCountUp(target: number, duration = 1800) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!ref.current || target === 0) return;
    const start = performance.now();
    const startVal = 0;

    const step = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(startVal + (target - startVal) * eased);
      if (ref.current) {
        ref.current.textContent = "$" + current.toLocaleString();
      }
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }, [target, duration]);

  return ref;
}

interface DonationTickerProps {
  variant?: "banner" | "compact" | "footer";
}

export function DonationTicker({ variant = "banner" }: DonationTickerProps) {
  const { data: total } = useQuery({
    queryKey: ["donation-ticker-total"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("donation_discount_requests")
        .select("donation_amount_cents")
        .eq("status", "approved");
      if (error) throw error;
      const sum = (data || []).reduce((acc, r) => acc + (r.donation_amount_cents || 0), 0);
      return Math.round(sum / 100); // dollars
    },
    staleTime: 5 * 60 * 1000,
  });

  const amountRef = useCountUp(total ?? 0);

  if (total === undefined || total === 0) return null;

  if (variant === "footer") {
    return (
      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-2">
        <Heart className="h-3.5 w-3.5 text-brand-pink fill-brand-pink/60 shrink-0" />
        <span>
          Go Fluff customers have donated{" "}
          <span ref={amountRef} className="font-semibold text-foreground">
            ${(total).toLocaleString()}
          </span>{" "}
          to animal rescues 🐾
        </span>
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-brand-pink/20 bg-brand-pink/5 px-4 py-2.5">
        <Heart className="h-4 w-4 text-brand-pink fill-brand-pink/60 shrink-0" />
        <p className="text-sm text-muted-foreground">
          Together, Go Fluff customers have donated{" "}
          <span ref={amountRef} className="font-semibold text-foreground">
            ${(total).toLocaleString()}
          </span>{" "}
          to animal rescues 🐾
        </p>
      </div>
    );
  }

  // banner variant (homepage)
  return (
    <div className="w-full py-2.5 px-4 bg-brand-pink/10 border-y border-brand-pink/20 text-center">
      <div className="flex items-center justify-center gap-2 flex-wrap">
        <Heart className="h-4 w-4 text-brand-pink fill-brand-pink/70 shrink-0 animate-pulse" />
        <p className="text-sm font-medium text-foreground">
          Go Fluff customers have donated{" "}
          <span ref={amountRef} className="text-brand-pink font-bold">
            ${(total).toLocaleString()}
          </span>{" "}
          to animal rescues — thank you! 🐾
        </p>
        <Heart className="h-4 w-4 text-brand-pink fill-brand-pink/70 shrink-0 animate-pulse" />
      </div>
    </div>
  );
}
